/* Vanilla SVG stand-in for the prototype's recharts AreaChart (Overview → AI visibility
   trend): magenta gradient area, dashed horizontal grid, mono ticks, hover tooltip.
   Same visual grammar: no axis lines, interval ≈ len/6 on X, ~4 nice ticks on Y. */

import { fmt, T, FONT, MONO, escapeHtml } from "./ui.js";

function niceTicks(max, count = 4) {
  if (!(max > 0)) return { top: 1, ticks: [0, 1] };
  const rough = max / count;
  const mag = Math.pow(10, Math.floor(Math.log10(rough)));
  let step = mag;
  for (const m of [1, 2, 5, 10]) { if (rough <= m * mag) { step = m * mag; break; } }
  const top = Math.ceil(max / step) * step;
  const ticks = [];
  for (let v = 0; v <= top + step / 2; v += step) ticks.push(v);
  return { top, ticks };
}

export function mountAreaChart(container, series) {
  if (!series || !series.length) { container.innerHTML = ""; return; }
  const W = Math.max(240, container.clientWidth || 420);
  const H = 150;
  const padL = 44, padR = 8, padT = 8, padB = 22;
  const iw = W - padL - padR, ih = H - padT - padB;
  const vals = series.map(p => p.v);
  const maxV = Math.max(...vals);
  const { top, ticks } = niceTicks(maxV);
  const X = i => padL + (series.length === 1 ? iw / 2 : (i / (series.length - 1)) * iw);
  const Y = v => padT + ih - (v / top) * ih;

  const pts = series.map((p, i) => [X(i), Y(p.v)]);
  const line = pts.map(([x, y], i) => `${i ? "L" : "M"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const area = `${line} L${pts[pts.length - 1][0].toFixed(1)},${(padT + ih).toFixed(1)} L${pts[0][0].toFixed(1)},${(padT + ih).toFixed(1)} Z`;

  const labelEvery = Math.max(1, Math.ceil(series.length / 6));
  const gradId = "gTrend";

  container.innerHTML = `
    <div style="position:relative">
      <svg id="trend-svg" width="100%" height="${H}" viewBox="0 0 ${W} ${H}" style="display:block">
        <defs>
          <linearGradient id="${gradId}" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stop-color="${T.magenta}" stop-opacity="0.18"/>
            <stop offset="100%" stop-color="${T.magenta}" stop-opacity="0"/>
          </linearGradient>
        </defs>
        ${ticks.map(v => `
          <line x1="${padL}" x2="${W - padR}" y1="${Y(v)}" y2="${Y(v)}" stroke="${T.line}" stroke-dasharray="3 3"/>
          <text x="${padL - 6}" y="${Y(v) + 3}" text-anchor="end" font-family="${MONO}" font-size="10" fill="${T.muted}">${fmt(v)}</text>`).join("")}
        <path d="${area}" fill="url(#${gradId})"/>
        <path d="${line}" fill="none" stroke="${T.magenta}" stroke-width="2"/>
        ${series.map((p, i) => (i % labelEvery === 0 || i === series.length - 1)
          ? `<text x="${X(i)}" y="${H - 6}" text-anchor="middle" font-family="${MONO}" font-size="10" fill="${T.muted}">${escapeHtml(String(p.d))}</text>`
          : "").join("")}
        <circle id="trend-dot" r="3.5" fill="${T.magenta}" stroke="#fff" stroke-width="1.5" style="display:none"/>
        <line id="trend-guide" y1="${padT}" y2="${padT + ih}" stroke="${T.line}" style="display:none"/>
        <rect id="trend-overlay" x="${padL}" y="0" width="${iw}" height="${H}" fill="transparent"/>
      </svg>
      <div id="trend-tip" style="display:none;position:absolute;pointer-events:none;background:${T.surface};border:1px solid ${T.line};border-radius:10;font-size:12;font-family:${FONT};box-shadow:0 8px 24px rgba(0,0,0,.08);padding:7px 11px;white-space:nowrap;z-index:5">
        <div style="font-family:${MONO};font-size:10;color:${T.muted}" id="trend-tip-d"></div>
        <div style="margin-top:2px;display:flex;align-items:center;gap:5px"><span style="width:7;height:7;border-radius:50%;background:${T.magenta};display:inline-block"></span><b>${fmt(0)}</b>&nbsp;AI impressions</div>
      </div>
    </div>`;

  const svg = container.querySelector("#trend-svg");
  const dot = container.querySelector("#trend-dot");
  const guide = container.querySelector("#trend-guide");
  const tipEl = container.querySelector("#trend-tip");

  svg.addEventListener("mousemove", e => {
    const rect = svg.getBoundingClientRect();
    const scale = W / rect.width;
    const mx = (e.clientX - rect.left) * scale;
    const i = Math.max(0, Math.min(series.length - 1, Math.round(((mx - padL) / iw) * (series.length - 1))));
    const [px, py] = pts[i];
    dot.setAttribute("cx", px); dot.setAttribute("cy", py); dot.style.display = "";
    guide.setAttribute("x1", px); guide.setAttribute("x2", px); guide.style.display = "";
    tipEl.style.display = "";
    tipEl.querySelector("b").textContent = fmt(series[i].v);
    tipEl.querySelector("#trend-tip-d").textContent = series[i].full || series[i].d;
    const wrap = container.firstElementChild.getBoundingClientRect();
    let left = (px / scale) + 12;
    if (left + 150 > wrap.width) left = (px / scale) - 162;
    tipEl.style.left = left + "px";
    tipEl.style.top = Math.max(0, py / scale - 54) + "px";
  });
  svg.addEventListener("mouseleave", () => {
    dot.style.display = "none"; guide.style.display = "none"; tipEl.style.display = "none";
  });
}
